import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Loader2, 
  Activity,
  AlertTriangle,
  CheckCircle2,
  ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AnalysisComplete() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  const analysisId = searchParams.get('analysis_id');

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (analysisId) {
      processAnalysis();
    } else {
      setError('No analysis ID provided');
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, analysisId]);

  const processAnalysis = async () => {
    try {
      const response = await fetch(`${API_URL}/api/analysis/${analysisId}/process`, {
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        toast.success('Analysis complete!');
      } else {
        setError(data.detail || 'Failed to process analysis');
      }
    } catch (err) {
      setError('Failed to process analysis');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-lg font-medium">Processing your analysis...</p>
            <p className="text-sm text-muted-foreground">This may take a few seconds</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="font-medium mb-2">Analysis Error</p>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => navigate('/analysis')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Analysis
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Success Header */}
          <div className="text-center mb-8">
            <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h1 className="font-display text-3xl font-bold uppercase tracking-tight mb-2">
              Analysis Complete
            </h1>
            <p className="text-muted-foreground">
              Your Pro Analysis results are ready
            </p>
          </div>

          {/* Result */}
          {result && (
            <Card className="border-primary/30 mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary" />
                  {result.analysis_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </CardTitle>
                <CardDescription>
                  Pro Analysis Result • ${result.cost} CAD
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <div className="whitespace-pre-wrap bg-secondary/50 p-4 rounded-sm text-sm leading-relaxed">
                    {result.result}
                  </div>
                </div>
                <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                  <p className="text-xs text-yellow-500 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {result.disclaimer}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline"
              onClick={() => navigate('/analysis')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Run Another Analysis
            </Button>
            <Button onClick={() => navigate('/board')}>
              View Games
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
